import axios from "axios";
import history from "./history";


const api = axios.create({
  baseURL: `/api`, // Replace with your server's URL
  withCredentials: true // Include cookies in requests
});


// Helper function to clear auth and redirect to login
const handleAuthFailure = (role) => {
  console.log("Removing from localStorage: cookie, username, webSocket, weSocketPort");
  localStorage.removeItem("cookie");
  localStorage.removeItem("username");
  localStorage.removeItem("webSocket");
  localStorage.removeItem("webSocketPort");
  // Redirect to the appropriate login page
  const loginPath = role === 'admin' ? '/admin' : '/operator';
  window.location.href = loginPath;
};

api.interceptors.response.use(
  response => response,
  error => {
    // Only intercept 401 errors from /api/admin or /api/operator
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || "";
      if (url.startsWith("/api/admin") || url.startsWith("/api/operator")) {
        // Unauthorized on operator or admin endpoints: clear auth data and redirect
        const role = url.startsWith("/api/admin") ? 'admin' : 'operator';
        handleAuthFailure(role);
        return Promise.reject(error);
      }
    }

    if (error.response && error.response.status === 400) {
      // Call your function here
      console.log("Status 400 error occurred");
    }
    return Promise.reject(error);
  }
);


/// Check if the user is unauthorized and handle redirection,
/// returning true if authorized, false if redirected.
const handleUserUnauthorizedAccess = response => {
  if (
    response.status === 400 ||
    (response.data &&
      response.data.message &&
      response.data.message === "User authentication failed")
  ) {
    // Perform the redirect or other actions
    // Handle other errors as needed
    history.push("/");
    window.location.reload();
    return false;
  }
  return true;
};


export const ChatAPI = {
  // Get raised hand questions
  getRaisedHandQuestions: async () => {
    const response = await api.get("/questionnaire/raised-hand-questions");
    if (!handleUserUnauthorizedAccess(response)) return null;
    return response.data;
  },

  // Raise hand (report bug)
  raiseHand: async (message, queueID = null) => {
    const response = await api.post("/questionnaire/raise-hand-question-request", {
      message,
      queueID,
    });
    if (!handleUserUnauthorizedAccess(response)) return null;
    return response.data;
  },

  // Close raised hand queue
  closeRaisedHandQueue: async (queueID) => {
    const response = await api.post("/questionnaire/raised-hand-question-closed", {
      queueID,
    });
    if (!handleUserUnauthorizedAccess(response)) return null;
    return response.data;
  },
}


/// Questionnaire API methods
export const QuestionnaireAPI = {
  // Get the current user questionnaire state
  state: async () => {
    const sentTime = Date.now();
    const response = await api.get("/questionnaire/state");
    if (!handleUserUnauthorizedAccess(response)) return null;

    const data = response.data;
    if (data.payload && typeof data.payload.timeLeft === 'number') {
      const receivedTime = Date.now();
      const rtt = (receivedTime - sentTime) / 1000; // Round-trip time, in seconds
      const latency = rtt / 2;
      data.payload.timeLeft = Math.max(0, data.payload.timeLeft - latency);
    }
    return data;
  },

  // Request to move to the next question or state
  next: async (currentPosition, targetPosition, confirm) => {
    const response = await api.post("/questionnaire/next", {
      currentPosition,
      targetPosition,
      confirm,
    });
    if (!handleUserUnauthorizedAccess(response)) return null;
    return response.data;
  },

  // Submit the user's answer for the current question
  submit: async (currentPosition, answer) => {
    const response = await api.post("/questionnaire/submit", {
      currentPosition,
      answer,
    });
    if (!handleUserUnauthorizedAccess(response)) return null;
    return response.data;
  },

  requestPause: async () => {
    try {
      const response = await api.post("/questionnaire/toilet-break-request");
      if (!handleUserUnauthorizedAccess(response)) return null;
      try {
        window.dispatchEvent(new CustomEvent('showGlobalMessage', { detail: { message: 'הבקשה נשלחה, הפסקה תחל כשתאושר', severity: 'info' } }));
      } catch (e) {
        // fallback
        alert('הבקשה נשלחה, הפסקה תחל כשתאושר');
      }
    } catch (error) {
      // If user is in a chapter without toilet brakes, show a non-blocking message instead
      if (error.response && error.response.data && error.response.data.message === "User is in chapter without toilet brakes") {
        try {
          window.dispatchEvent(new CustomEvent('showGlobalMessage', { detail: { message: 'לא ניתן לצאת לשירותים בפרק זה', severity: 'warning' } }));
        } catch (e) {
          // fallback
          alert('לא ניתן לצאת לשירותים בפרק זה');
        }
        return;
      }
      history.push("/");
      window.location.reload();
      console.error("Error sending toilet request", error);
    }
  },

  /**
   * Tell the server we're still here, and pick up operator popups the WebSocket
   * didn't deliver.
   *
   * `lastPopupId` is the newest popup the test-taker has dismissed; the server keeps
   * re-sending anything newer, so nothing is lost if a response never arrives.
   *
   * Failures are swallowed: a missed heartbeat only means the operator briefly sees
   * the user as offline, and it must never interrupt the test.
   *
   * The response also carries the server's current position for this user, so a page can
   * follow an operator-initiated move without waiting for the test-taker to press "next".
   *
   * @param {number} [lastPopupId=0]
   * @returns {Promise<{popups: {id: number, message: string}[], position: Object|null}>}
   */
  heartbeat: async (lastPopupId = 0) => {
    try {
      const response = await api.post("/questionnaire/heartbeat", { lastPopupId });
      return {
        popups: response.data?.popups || [],
        position: response.data?.position || null,
      };
    } catch (error) {
      console.warn("Heartbeat failed:", error?.message);
      return { popups: [], position: null };
    }
  },

  // Resume the questionnaire from a paused state
  resume: async () => {
    const response = await api.post("/questionnaire/resume");
    if (!handleUserUnauthorizedAccess(response)) return null;
    return response.data;
  },
};

export default api;
